"use client"

import { useEffect, useState } from "react"
import { Mail, X, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

const DISMISS_KEY = "rmar_review_signup_dismissed"

/**
 * Contextual email capture shown to logged-out visitors who've read a couple of
 * reviews — a high-intent moment. Framed around the review content they're
 * already engaging with. Dismissible and remembered in localStorage so it isn't
 * naggy; subscribes via the existing (rate-limited) newsletter endpoint, tagged
 * with a source so the funnel is measurable.
 */
export function ReviewEngagementSignup({ residencyName }: { residencyName: string }) {
  // Visible by default so a first-time (logged-out) visitor sees it on first paint
  // with no pop-in. localStorage is client-only, so a prior dismissal is applied
  // after mount — a rare flash-out for repeat visitors who already dismissed it.
  const [dismissed, setDismissed] = useState(false)
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const [message, setMessage] = useState("")

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDismissed(true)
      }
    } catch {
      /* ignore storage failures — stay visible */
    }
  }, [])

  function dismiss() {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISS_KEY, "1")
    } catch {
      /* ignore storage failures */
    }
  }

  async function subscribe(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setStatus("loading")
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "residency_reviews" }),
      })
      const data = await res.json()
      if (res.ok) {
        setStatus("success")
      } else {
        setStatus("error")
        setMessage(data.error || "Something went wrong")
      }
    } catch {
      setStatus("error")
      setMessage("Something went wrong. Please try again.")
    }
  }

  if (dismissed) return null

  return (
    <div className="relative border border-[var(--border)] rounded-xl p-5 bg-[var(--secondary)] my-6">
      <button
        type="button"
        onClick={dismiss}
        title="Dismiss"
        className="absolute top-3 right-3 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
      >
        <X size={15} />
      </button>

      {status === "success" ? (
        <div className="flex items-center gap-2 text-sm text-emerald-700 pr-6">
          <Check size={16} />
          You&apos;re on the list — honest reviews and fresh open calls, straight to your inbox.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-1">
            <Mail size={15} className="text-[var(--primary)]" />
            <p className="font-semibold text-sm">Reviews you can trust — before you apply</p>
          </div>
          <p className="text-[13px] text-[var(--muted-foreground)] mb-3 leading-relaxed pr-6">
            Get RMAR&apos;s newsletter: new artist-written reviews, open calls, and residency
            guides for programs like {residencyName}. No spam, unsubscribe anytime.
          </p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 bg-white"
              required
            />
            <Button type="submit" size="sm" disabled={status === "loading" || !email}>
              {status === "loading" ? "..." : "Subscribe"}
            </Button>
          </form>
          {status === "error" && <p className="text-xs text-red-600 mt-2">{message}</p>}
        </>
      )}
    </div>
  )
}
